// The guided tour of Talent Acquisition, for the coordinator and hiring
// managers meeting the pipeline board for the first time.
//
// Steps point at [data-tour] anchors on TalentAcquisitionPage, in the order the
// page reads: header and actions, filters, tiles, the board, then the candidate
// list and the vacancy workspace. The engine (shared/FeatureTour.jsx) and the show-once record
// (hooks/useFeatureTour.js) are generic; only this file knows about vacancies.

export const TALENT_TOUR_ID = 'talent-acquisition';

export const talentTourSteps = [
  {
    title: 'Welcome to Talent Acquisition',
    target: '[data-tour="page-header"]',
    body: [
      'Vacancies and candidates now live here, one step before Onboarding. This one-minute tour shows the pipeline board, how to move a candidate along, and where each vacancy is managed.',
      'Use Next (or the arrow keys) to walk through, or Skip if you would rather explore. Either way it will not pop up again.',
    ],
  },
  {
    title: 'Raise a vacancy',
    target: '[data-tour="raise-vacancy"]',
    body: [
      'A vacancy starts here: the role, team, hiring manager, headcount and when it needs filling. It goes to the approver, and candidates can be added once it is approved.',
      'Approvers decide from the Hiring tab on Approvals, under My Space. The outcome shows against the vacancy in the workspace, and whoever raised it is emailed the decision.',
    ],
  },
  {
    title: 'Filter the board',
    target: '[data-tour="filters"]',
    body: [
      'Show one vacancy or all of them, and tick "Only stalled" to see just the candidates who have sat in a stage for more than a week.',
      'Candidates who have accepted an offer stay in the Accepted column while "Include accepted" is ticked, which it is by default. Tick "Include rejected" to bring back the rejected and withdrawn ones too.',
    ],
  },
  {
    title: 'The numbers that matter',
    target: '[data-tour="tiles"]',
    body: [
      'Open vacancies, active candidates and how many have stalled. They follow the filter above, so a single vacancy shows its own figures.',
    ],
  },
  {
    title: 'The pipeline board',
    target: '[data-tour="board"]',
    body: [
      'Each candidate is a card in the column for their stage: Applied, Screening, Interview 1, Interview 2, Offer and Accepted. Drag a card to the next column, or click it to open the panel and move it from there.',
      'A card with an amber warning has been in its stage for more than seven days. Every move is recorded with who made it and when.',
    ],
  },
  {
    title: 'Book interviews from the candidate',
    target: '[data-tour="candidate-panel"]',
    body: [
      'Open a candidate and use Book interview: pick the type, the time, where it is and who is on the panel. Invites go to the panel and the candidate, and each interviewer gets an Action due the next working day.',
      'Interviewers record a recommendation on their own feedback screen, reached from the Action or the email, so they do not need this page. Their verdicts show here against the interview.',
    ],
  },
  {
    title: 'Make an offer, then hand over',
    target: '[data-tour="candidate-panel"]',
    when: (c) => c.canManage,
    body: [
      'From the same panel, Make offer records the start date, clearance and conditions, and whether it is permanent, fixed term or contractor. Permanent and fixed term carry a salary, and fixed term also the date the contract ends; a contractor has a day rate and IR35 status. Within the vacancy\'s salary or day-rate band it is approved straight away; above it the offer waits for the offer approver on the Hiring tab of Approvals.',
      'Mark it sent once the letter has gone, then record the outcome. Accepted creates the person and their onboarding record, with Offer and Contract already complete and a fixed-term contract\'s end date as their End of Employment, and offers to let the other candidates know when the vacancy fills.',
    ],
  },
  {
    title: 'Erase a candidate for a GDPR request',
    target: '[data-tour="delete-candidate"]',
    when: (c) => c.canDelete,
    body: [
      'Delete permanently, at the foot of the candidate panel, is for a GDPR erasure request. It removes the candidate and everything held about them: interviews and panel feedback, offers, documents and the CV, notes and history. It cannot be undone, and the dialog asks you to type DELETE first.',
      'It is not the same as Reject, which takes somebody off the board and keeps the record. The audit trail keeps what was done and by whom, with their name and details removed, and records who erased them and when. A hired candidate cannot be erased from here, as their record now lives in People and Onboarding.',
    ],
  },
  {
    title: 'Everyone who has applied',
    target: '[data-tour="view-toggle"]',
    body: [
      'Candidates lists everyone on one table rather than as cards: filter to Active, Rejected, Hired or All, narrow to one vacancy, and search by name, email, job title or vacancy reference.',
      'It is the way back to somebody who has left the board. Click a row to open the same panel the board opens, where Edit under Details corrects their name, contact details, job title and source.',
    ],
  },
  {
    title: 'The vacancy workspace',
    target: '[data-tour="view-toggle"]',
    body: [
      'Switch to Vacancies to see one role at a time: its approval, headcount and band, the funnel of candidates through each stage, the candidate table and what has happened recently.',
      'Hiring managers granted this page see only the vacancies they manage, and see the salary band only if they have also been given salaries in Admin → Access.',
    ],
  },
  {
    title: 'Close and reopen a vacancy',
    target: '[data-tour="view-toggle"]',
    when: (c) => c.canManage,
    body: [
      'Close takes a role off the market; it fills by itself once the last offer is accepted. Tick "Show closed, filled and rejected" in the workspace to find those roles again.',
      'Reopen brings one back when you need somebody (else) after all: say how many more people and why. Within the headcount already approved it opens straight away; asking for more goes to the vacancy approver on the Hiring tab of Approvals, and it stays as it was until they agree.',
    ],
  },
  {
    title: 'Reports and retention',
    target: '[data-tour="view-toggle"]',
    when: (c) => c.canManage,
    body: [
      'Reports shows the whole pipeline: time to hire, how long candidates sit in each stage, where hires come from, offer acceptance and how quickly interviewers give feedback.',
      'It also shows the retention position. Unsuccessful candidates who did not ask to be kept on file are anonymised six months after their vacancy closes, nightly; you can run that early from here, and mark "Keep on file" on a candidate to hold them back.',
    ],
  },
];
